import { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '@/modules/admin/stores/auth';
import { useToast } from 'vue-toastification';
import { capitalizeFirstLetter } from '@/utils';

let toastInstance: ReturnType<typeof useToast> | null = null;

export const initToast = (toast: ReturnType<typeof useToast>) => {
  toastInstance = toast;
};

export const setupInterceptors = (apiClient: AxiosInstance) => {
  // Attach the JWT as a Bearer token when present. The httpOnly cookie still
  // works same-site (desktop), but is blocked cross-site on iOS/Safari
  // (*.onrender.com), so the header is the reliable auth path there.
  apiClient.interceptors.request.use((config) => {
    const token = useAuthStore().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  apiClient.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const authStore = useAuthStore();
      const status = error.response?.status;

      if (status === 400 || status === 401 || status === 403) {
        const errorData = error.response?.data as any;
        const toast = toastInstance || useToast();

        // NestJS flat format: { message: string | string[], error: string, statusCode: number }
        if (Array.isArray(errorData?.message)) {
          errorData.message.forEach((msg: string) => {
            toast.error(capitalizeFirstLetter(msg));
          });
        } else {
          const msg = errorData?.message || errorData?.error?.message || 'An error occurred';
          toast.error(capitalizeFirstLetter(msg));
        }

        if (status === 401) {
          authStore.logoutUser();
        }
      }

      if (status === 404) {
        const requestUrl = error.config?.url || '';
        const baseURL = error.config?.baseURL || '';
        const fullUrl = (baseURL + requestUrl) || requestUrl;
        console.error('API 404 error:', error.config?.method?.toUpperCase(), fullUrl, error.response?.data);
      }

      return Promise.reject(error);
    }
  );
};

